import { useCallback } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Navigation } from './Navigation';
import { useAuthStore } from '../store/authStore';
import { AUTH_USER_QUERY_KEY } from '../hooks/useAuthUser';
import { useTheme } from './ThemeProvider';
import { Button } from '@werkstatt/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '@werkstatt/ui';
import { Avatar, AvatarFallback, AmbientBackground } from '@werkstatt/ui';
import { Clock, LogOut, User as UserIcon, Sun, Moon, Monitor } from 'lucide-react';
import { toast } from 'sonner';
import { getUserInitials } from '../lib/utils';

interface Props {
  children: ReactNode;
  title?: string;
  showNavigation?: boolean;
}

export function MainLayout({ children, title = 'Zeiterfassung @ Werkstatt-Connect', showNavigation = true }: Props) {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { theme, setTheme } = useTheme();
  const queryClient = useQueryClient();
  const shouldShowNavigation = showNavigation && user;

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      // Verhindert, dass useAuthUser()/ProtectedRoute nach dem Logout noch
      // kurz den alten Nutzer aus dem Query-Cache zeigt.
      queryClient.removeQueries({ queryKey: AUTH_USER_QUERY_KEY });
      toast.success('Erfolgreich abgemeldet');
      navigate('/login');
    } catch {
      toast.error('Fehler beim Abmelden');
    }
  }, [navigate, logout, queryClient]);

  return (
    <div className="min-h-screen bg-background">
      <AmbientBackground />
      <header className="backdrop-blur-lg bg-[rgb(var(--sphere-card)_/_var(--sphere-card-alpha))] border-b border-[rgb(var(--sphere-border)_/_var(--sphere-border-alpha))] px-6 py-4 sticky top-0 z-20">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Clock className="h-8 w-8 text-primary" />
            <h1 className="font-display text-2xl font-bold text-foreground">{title}</h1>
          </div>

          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {getUserInitials(user.first_name, user.last_name, user.email)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <div className="flex flex-col space-y-1 p-2">
                  <p className="text-sm font-medium leading-none">{user.first_name} {user.last_name}</p>
                  <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {user.role === 'admin' ? 'Administrator' : 'Benutzer'}
                  </p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/account')}>
                  <UserIcon className="mr-2 h-4 w-4" />
                  <span>Konto</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground">Theme</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setTheme('light')}>
                  <Sun className="mr-2 h-4 w-4" />
                  <span>Hell</span>
                  {theme === 'light' && <span className="ml-auto">✓</span>}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('dark')}>
                  <Moon className="mr-2 h-4 w-4" />
                  <span>Dunkel</span>
                  {theme === 'dark' && <span className="ml-auto">✓</span>}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('system')}>
                  <Monitor className="mr-2 h-4 w-4" />
                  <span>System</span>
                  {theme === 'system' && <span className="ml-auto">✓</span>}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Abmelden</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </header>

      <div className="flex h-[calc(100vh-73px)]">
        {shouldShowNavigation && (
          <div className="flex-shrink-0 p-3">
            <Navigation />
          </div>
        )}
        <main className="flex-1 overflow-auto">
          <div className="container mx-auto px-6 py-8">{children}</div>
        </main>
      </div>
    </div>
  );
}
